<template>
  <div class="app-container">
    <div class="et-title">邮件模板管理</div>

    <!-- 列表 -->
    <el-table :data="templates" v-loading="loading" border style="width:100%">
      <el-table-column label="模板代码" width="180">
        <template slot-scope="scope">
          <el-tag type="primary" size="small">{{ scope.row.templateCode }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="name" label="名称" width="200" show-overflow-tooltip />
      <el-table-column label="收件人" min-width="180">
        <template slot-scope="scope">
          <span v-if="!scope.row.notifyTo" class="et-secondary">使用品牌默认</span>
          <template v-else-if="notifyEntries(scope.row.notifyTo)">
            <div v-for="entry in notifyEntries(scope.row.notifyTo)" :key="entry[0]">
              <el-tag size="small">{{ entry[0] }}</el-tag> {{ entry[1] }}
            </div>
          </template>
          <span v-else>{{ scope.row.notifyTo }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="description" label="说明" min-width="160" show-overflow-tooltip />
      <el-table-column label="最后修改" width="180">
        <template slot-scope="scope">{{ scope.row.updatedAt ? formatDate(scope.row.updatedAt) : '-' }}</template>
      </el-table-column>
      <el-table-column label="操作" width="90">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openEdit(scope.row)"><i class="el-icon-edit"></i> 编辑</el-button>
        </template>
      </el-table-column>
      <template slot="empty">暂无模板</template>
    </el-table>

    <!-- 编辑弹窗 -->
    <el-dialog :title="editing ? ('编辑模板 - ' + editing.name) : '编辑模板'" :visible.sync="modalOpen" width="720px">
      <div v-if="editing" class="et-vars">
        <span class="et-secondary">可用变量：</span>
        <div class="et-var-tags">
          <el-tag
            v-for="v in variableList(editing.availableVariables)"
            :key="v"
            type="primary"
            class="et-var-tag"
            @click="insertVariable(v)"
          >{{ varLabel(v) }}</el-tag>
        </div>
        <span class="et-secondary et-var-tip">点击变量标签可快速插入到内容末尾</span>
      </div>
      <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
        <el-form-item label="Default 品牌收件人" prop="notifyToDefault">
          <el-input v-model="form.notifyToDefault" placeholder="多个以逗号分隔，留空则使用 YAML 品牌预设" />
        </el-form-item>
        <el-form-item label="CX 品牌收件人" prop="notifyToCx">
          <el-input v-model="form.notifyToCx" placeholder="多个以逗号分隔，留空则使用 YAML 品牌预设" />
        </el-form-item>
        <el-form-item label="邮件主旨" prop="subjectTemplate">
          <el-input v-model="form.subjectTemplate" :placeholder="subjectPh" />
        </el-form-item>
        <el-form-item label="HTML 内容" prop="bodyTemplate">
          <el-input
            v-model="form.bodyTemplate"
            type="textarea"
            :rows="14"
            :placeholder="bodyPh"
            class="et-mono"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="modalOpen = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="handleSave">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listEmailTemplates, updateEmailTemplate } from '@/api/customerService/emailTemplate'

export default {
  name: 'CustomerServiceEmailList',
  data() {
    return {
      subjectPh: '支持 {{变量}} 占位符',
      bodyPh: '支持 HTML + {{变量}} 占位符',
      templates: [],
      loading: false,
      modalOpen: false,
      editing: null,
      saving: false,
      form: {
        subjectTemplate: '',
        bodyTemplate: '',
        notifyToDefault: '',
        notifyToCx: ''
      },
      rules: {
        subjectTemplate: [{ required: true, message: '请输入主旨', trigger: 'blur' }],
        bodyTemplate: [{ required: true, message: '请输入内容', trigger: 'blur' }]
      }
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true
      listEmailTemplates().then(res => {
        if (res.code === 0) this.templates = res.result || []
      }).catch(() => {
        this.$message.error('加载模板失败')
      }).finally(() => { this.loading = false })
    },
    parseNotifyTo(raw) {
      if (!raw) return { default: '', cx: '' }
      const trimmed = String(raw).trim()
      if (trimmed.startsWith('{')) {
        try {
          const obj = JSON.parse(trimmed)
          return { default: obj.default || '', cx: obj.cx || '' }
        } catch (e) { /* ignore */ }
      }
      return { default: trimmed, cx: '' }
    },
    notifyEntries(raw) {
      try {
        const obj = JSON.parse(raw)
        const arr = []
        Object.keys(obj || {}).forEach(k => { arr.push([k, obj[k]]) })
        return arr
      } catch (e) {
        return null
      }
    },
    variableList(raw) {
      return String(raw || '').split(',').map(v => v.trim()).filter(Boolean)
    },
    varLabel(v) {
      return '{{' + v + '}}'
    },
    formatDate(v) {
      if (!v) return '-'
      const d = new Date(v)
      if (isNaN(d)) return String(v)
      const p = n => (n < 10 ? '0' + n : '' + n)
      return d.getFullYear() + '/' + p(d.getMonth() + 1) + '/' + p(d.getDate()) + ' ' + p(d.getHours()) + ':' + p(d.getMinutes())
    },
    openEdit(record) {
      this.editing = record
      const recipients = this.parseNotifyTo(record.notifyTo)
      this.form.subjectTemplate = record.subjectTemplate
      this.form.bodyTemplate = record.bodyTemplate
      this.form.notifyToDefault = recipients.default
      this.form.notifyToCx = recipients.cx
      this.modalOpen = true
    },
    insertVariable(v) {
      this.form.bodyTemplate = (this.form.bodyTemplate || '') + this.varLabel(v)
    },
    handleSave() {
      if (!this.editing) return
      this.$refs.formRef.validate(valid => {
        if (!valid) return
        this.saving = true
        const notifyToObj = {}
        if (this.form.notifyToDefault) notifyToObj.default = this.form.notifyToDefault
        if (this.form.notifyToCx) notifyToObj.cx = this.form.notifyToCx
        const notifyTo = Object.keys(notifyToObj).length > 0 ? JSON.stringify(notifyToObj) : ''
        updateEmailTemplate(this.editing.id, {
          subjectTemplate: this.form.subjectTemplate,
          bodyTemplate: this.form.bodyTemplate,
          notifyTo: notifyTo
        }).then(res => {
          if (res.code === 0) {
            this.$message.success('模板已更新')
            this.modalOpen = false
            this.loadData()
          }
        }).catch(() => {
          this.$message.error('保存失败')
        }).finally(() => { this.saving = false })
      })
    }
  }
}
</script>

<style scoped lang="scss">
.et-title { font-size: 20px; font-weight: 600; margin-bottom: 16px; }
.et-secondary { color: #909399; }
.et-vars { margin-bottom: 16px; }
.et-var-tags { margin-top: 4px; display: flex; flex-wrap: wrap; gap: 4px; }
.et-var-tag { cursor: pointer; }
.et-var-tip { font-size: 12px; margin-left: 8px; }
.et-mono ::v-deep  textarea { font-family: monospace; }
</style>